import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import PortalParcelMap, { type ParcelFeatureCollection } from '../components/portal/PortalParcelMap';
import ComparableSales, { type CompsBlock } from '../components/portal/ComparableSales';
import PortalNotes from '../components/portal/PortalNotes';
import PortalSectionNav, { type PortalPhoneMenu } from '../components/portal/PortalSectionNav';
import { exchangeRows, todayIso, type PortalExchange } from '../components/portal/exchangeClock';
import LeaseSearchCardBody from '../components/portal/LeaseSearchCardBody';
import MapLetter from '../components/portal/MapLetter';
import LeaseSearchTable, { type GlanceGroup } from '../components/portal/LeaseSearchTable';
import { KIND_WORDS, isSpecSearch, pipelineKindOf, type LeaseFact, type ScorecardRowDef } from '../components/portal/leaseSearch';
import BuySearchTab, { buySearchNavEntries } from '../components/portal/BuySearchTab';
// PortalTabBar, never "PortalTabs": on a case-insensitive disk that path resolves to portalTabs.ts, the tab MODEL.
import PortalTabBar, { portalTabId, portalTabPanelId } from '../components/portal/PortalTabBar';
import { projectForTab, tabForHash, tabsOf, type PortalTab, type PortalTabKey } from '../components/portal/portalTabs';
import PortalTermsTable from '../components/portal/PortalTermsTable';
import { omCapParts, omCapText, ourCapParts, ourCapText, type PortalDealTerms, type PortalOmCap, type TermsRowDeal } from '../components/portal/termsTable';
import CapText from '../components/portal/CapText';
import PrintChooser from '../components/portal/PrintChooser';
import { dealMark, docIsNew, sinceLine } from '../components/portal/sinceLastVisit';
import './ClientPortal.css';

/**
 * The client portal - `/portal/:token`, the app's first client-facing surface.
 *
 * THREE RULES THIS FILE LIVES BY:
 *
 * 1. It is a TIER-BLIND RENDERER. The server has already stripped everything the link's tier
 *    may not see (portalTierFilter.ts), so a lower tier arrives here as MISSING SECTIONS -
 *    no netProceeds key at all, parcels without `taxStatus`, a shorter documents list. Every
 *    section therefore renders conditionally and the page must look finished with any
 *    subset. A section that assumes its data exists is a blank page for a buyer link.
 *
 * 2. It HARDCODES NOTHING about any particular deal. Field names from the deal.json contract
 *    (schemaVersion 1.0) are fair game; a fact about a client is a bug. Acceptance for the
 *    whole feature is that a second deal row renders a second portal with zero code changes.
 *
 * 3. It NEVER RE-DERIVES A NUMBER OR A CLAIM. The JSON carries confidence-labelled copy
 *    ("REPORTED", "UNVERIFIED - pending the operator P&L"); those strings are printed
 *    verbatim. Business value in particular is shown as pending and additive, never folded
 *    into a headline price - overstating it in front of a family is the failure mode that
 *    matters here.
 */

/* ─────────────────────────────────── payload types ─────────────────────────────────── */

interface PortalBroker {
  name?: string;
  title?: string;
  firm?: string;
  dre?: string;
  phone?: string;
  email?: string;
  web?: string;
}

interface PortalParcel {
  apn?: string;
  county?: string;
  acres?: number;
  owner?: string;
  group?: string;
  use?: string;
  assessedValue?: number;
  isAnchor?: boolean;
  taxStatus?: { value?: string; tier?: string };
}

interface PortalParcelGroup {
  label?: string;
  description?: string;
  mapColor?: string;
  /** Display order (pipeline portals). Needed because jsonb does not keep key order. */
  order?: number;
  /** Schema 1.3 (a lease search): the one group that renders above the map - the best matches. */
  lead?: boolean;
}

interface PortalValuation {
  method?: string;
  realEstateLow?: number;
  realEstateHigh?: number;
  recommendedList?: number;
  impliedPerAcre?: number;
  businessValueLow?: number;
  businessValueHigh?: number;
  businessValueStatus?: string;
  allInLow?: number;
  allInHigh?: number;
  /** The multi-position shape. Carries its own tier, so the server may strip it for a buyer
   *  link while the rest of the valuation still serves. */
  businessValue?: {
    label?: string;
    status?: string;
    note?: string;
    views?: Array<{
      label?: string;
      low?: number;
      high?: number;
      allInLow?: number;
      allInHigh?: number;
      basis?: string;
    }>;
  };
  note?: string;
}

/*
  schemaVersion 1.2 (Vin's portal) - a BUYER'S PIPELINE beside the land-assemblage shape. Every field
  is a string quoted from the broker's own email or the delivered PDF ("6.26% pro forma / 3.77% in
  place"); the page prints them and derives nothing. `status.evidence` names the thread and who wrote
  last, and is printed verbatim under "Where this stands" - a status without its evidence is a guess.
*/
interface PortalPipelineDeal {
  /** VP8.5: the importer's stamps - when the deal reached the portal, and when its content last changed. */
  addedAt?: string | null;
  changedAt?: string | null;
  id: string;
  name?: string;
  tenant?: string | null;
  role?: string;
  address?: string;
  city?: string;
  county?: string;
  region?: string | null;
  propertyType?: string | null;
  ask?: string | null;
  cap?: string | null;
  verdict?: string | null;
  verdictSource?: string | null;
  analysisDate?: string | null;
  status?: {
    state?: string;
    group?: string;
    label?: string;
    asOf?: string | null;
    evidence?: string | null;
    /** The audit trail (Gmail ids, what was searched, notes to self). Tiered internal in the contract;
     *  the server strips it below that tier, so a client link never carries it. */
    audit?: { tier?: string; evidence?: string | null } | null;
  };
  loiHistory?: Array<{ date?: string; event?: string; by?: string }>;
  /** Ids into the contract's `documents[]` (the manifest), NOT portal_documents row ids. */
  documents?: string[];
  omHeld?: string | null;
  sentToVin?: string | null;
  /** The contract says which deals had a cover cut, so a card without one never asks for it. */
  hasCover?: boolean;
  /** VP8.4: NOI, building SF, price per SF and the five floors, quoted from ONE of the deal's analyses. */
  terms?: PortalDealTerms | null;
  /** VP8.4 round eight: the cap the seller's OM prints, and the price it prints it at. */
  omCap?: PortalOmCap | null;
  /** Schema 1.3 (a lease search): the building's facts as display strings, in the contract's order. */
  facts?: LeaseFact[] | null;
  /** Schema 1.3: one sentence saying where those facts come from ("As listed, ..."). */
  factsBasis?: string | null;
  /** Schema 1.4: the suite, kept apart from the street address the pin geocodes ("Unit 110"). */
  suite?: string | null;
  /** Schema 1.4: the letter on the client's printed tour sheet - on the card, the at-a-glance list and the pin. */
  mapLetter?: string | null;
  /** Schema 1.4: what Apex is still confirming on this building, printed as "We're confirming". */
  toConfirm?: string[] | null;
}

interface PortalPipeline {
  /** Schema 1.3: absent is a buyer's pipeline; 'lease-search' is a tenant's (components/portal/leaseSearch.ts); BUY1:
   *  'buy-search' is a buyer's building search, drawn on its own Buying tab (BuySearchTab). */
  kind?: string;
  /** Schema 1.3: the rows a lease search scores every building on, declared once and in order. */
  scorecard?: { rows?: ScorecardRowDef[] };
  asOf?: string;
  groups?: Record<string, PortalParcelGroup>;
  deals?: PortalPipelineDeal[];
}

interface PortalTimelineItem {
  /** VP9: an entry added in the app carries its own id (never shown). */
  id?: string;
  date?: string;
  kind?: string;
  title?: string;
  dealIds?: string[];
  /** VP9: when the entry reached the portal - the page marks it New after the reader's last visit. */
  addedAt?: string | null;
}

/** A manifest entry from the contract's `documents[]`; the page joins it to a served row BY TITLE,
 *  which is also how the loader attaches bytes - one key, both sides. */
interface PortalManifestDoc {
  id?: string;
  title?: string;
  category?: string;
  status?: string;
  dealId?: string;
}

interface PortalDeal {
  schemaVersion?: string;
  meta?: {
    dealName?: string;
    dealType?: string;
    status?: string;
    preparedBy?: string;
    asOfDate?: string;
    confidentialityNotice?: string;
    /** Schema 1.3: sentences for the legal footer ("... is not the listing broker ..."). */
    disclosures?: string[];
    /** AREA1: the area builder asks for a plain Call / Text band; absent on every other portal. */
    callButton?: boolean;
    /** BUY1: the page's tabs - exactly the property, then "Buying" (`tabsOf` reads it; anything else is no tabs). */
    tabs?: Array<{ key?: string; label?: string }>;
  };
  brokers?: PortalBroker[];
  property?: Record<string, unknown> & { headline?: string };
  parcels?: PortalParcel[];
  parcelGroups?: Record<string, PortalParcelGroup>;
  valuation?: PortalValuation;
  comps?: CompsBlock;
  netProceeds?: Record<string, unknown>;
  /* `id` anchors a note to ONE item (VP8.1a) and `dealId`, when present, names the card the item is
     about. Both optional: a portal whose contract predates them still renders, without per-item
     notes and without the jump - the same rule every other block on this page follows. */
  openItems?: Array<{
    id?: string;
    dealId?: string;
    item?: string;
    why?: string;
    owner?: string;
    /** VP9: a closed item stays in the contract (its notes stay attached; Undo reopens it) and is not rendered. */
    closedAt?: string | null;
    addedAt?: string | null;
    changedAt?: string | null;
  }>;
  buyBox?: Record<string, unknown>;
  /** Schema 1.3: asking rents by area, as the broker curated them. 1.4 adds a heading, the budget line, a segment and
   *  a typical rate per row, and the broker's takeaways - all printed as authored. */
  marketContext?: {
    asOf?: string;
    title?: string;
    intro?: string;
    budgetLine?: string;
    rows?: Array<{ segment?: string; area?: string; directAsking?: string; typical?: string; basis?: string }>;
    takeaways?: string[];
    footnote?: string;
  };
  pipeline?: PortalPipeline;
  timeline?: { items?: PortalTimelineItem[] };
  documents?: PortalManifestDoc[];
}

export interface PortalDocument {
  id: string;
  title?: string;
  category?: string | null;
  doc_date?: string | null;
  status?: 'available' | 'pending';
  sort_order?: number;
  /** VP8.5: when this document became available to open on the portal. */
  addedAt?: string | null;
}

export interface PortalPayload {
  slug?: string;
  title?: string;
  tier?: string;
  deal?: PortalDeal;
  parcelsGeojson?: { type?: string; features?: unknown[] } | null;
  documents?: PortalDocument[];
  /** VP8.5: when this link's previous visit ended; null on a first visit, so nothing is marked. */
  lastVisitAt?: string | null;
  notes?: Array<{
    id: string;
    anchorType: string;
    anchorKey: string;
    authorName: string;
    body: string;
    createdAt: string;
  }>;
}

/** One note exactly as the payload carries it - the element type of `PortalPayload.notes`. */
type PortalNoteRow = NonNullable<PortalPayload['notes']>[number];

/* ──────────────────────────────────── formatting ───────────────────────────────────── */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const money = (n: unknown): string | null =>
  typeof n === 'number' && Number.isFinite(n) ? USD.format(n) : null;

const decimal = (n: number): string =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n);

/** "$9,500,000 – $12,400,000", or the single end that exists, or null when neither does. */
const moneyRange = (low: unknown, high: unknown): string | null => {
  const a = money(low);
  const b = money(high);
  if (a && b) return `${a} – ${b}`;
  return a ?? b ?? null;
};

/**
 * Labels the generic humanizer cannot derive - acronyms and hyphenated terms the reader says as
 * such. "Dscr" and "Five Year Irr" reached Vin's page from the buy-box keys (found by LOOKING,
 * 2026-09-10); a key not listed here still falls through to the generic rule below.
 */
const KEY_LABELS: Record<string, string> = {
  dscr: 'DSCR',
  goingInCap: 'Going-in cap',
  fiveYearIrr: 'Five-year IRR',
  equityMultiple: 'Equity multiple',
  averageCashYield: 'Average cash yield',
  preferredAssets: 'Preferred assets',
};

/** camelCase -> "Camel case", for the generic property blocks whose keys vary by deal. */
const humanize = (key: string): string =>
  KEY_LABELS[key] ??
  key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2') // taxDeferredVia1031 -> "... Via 1031"
    .replace(/^./, (c) => c.toUpperCase());

/*
  A pipeline group's name, wherever it is printed: its heading in Buildings, its heading in the phone's at-a-glance
  list, and its entry in the phone menu (TP7). One function, so the three cannot disagree.
*/
const groupLabel = (key: string, group: PortalParcelGroup | undefined): string => group?.label || humanize(key || 'other');

/**
 * AREA1 (2026-10-26) - the Call / Text band an area page asks for: the first broker whose phone is a US 10-digit
 * number, as `tel:` / `sms:` links. Null when none parses, so a bad phone never draws a dead button.
 */
const callTarget = (brokers: PortalBroker[]): { first: string; e164: string } | null => {
  for (const b of brokers) {
    const digits = (b.phone ?? '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
    if (digits.length === 10) return { first: (b.name ?? '').trim().split(/\s+/)[0] || 'us', e164: `+1${digits}` };
  }
  return null;
};

/** The DOM id a group's accordion carries on a lease search - what the phone menu's group entries jump to. */
const groupElementId = (key: string): string => `group-${key || 'other'}`;

/** Keys whose numbers are dollars. Everything else stays a plain number (acres, counts). */
/*
  Keys whose numbers are dollars. Everything else stays a plain number (acres, counts, SF).

  `acre` was here once and matched any key CONTAINING it, so `totalCupAreaAcres` printed as
  "$107" - an acreage, rendered as money AND rounded, on a page the client reads. It bought
  nothing: `impliedPerAcre` is formatted explicitly below, and the comps table has its own
  formatters. `cash` is here because `cashOutNet` names neither a price nor a value and
  printed bare in a column of dollar figures.

  This is a heuristic on key NAMES, so it will keep being wrong at the edges. A new numeric
  field in the contract deserves a look at how it renders before it reaches a client.
*/
const MONEY_KEY = /(price|value|cost|proceeds|tax|basis|realized|reinvested|deferred|low|high|list|cash)/i;

const scalarText = (key: string, value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return MONEY_KEY.test(key) ? (money(value) as string) : decimal(value);
  }
  return String(value);
};

/* ───────────────────────────────── generic fact list ───────────────────────────────── */

/** A sentence-length value reads left-aligned; a figure keeps the right edge (found by LOOKING, 2026-09-10). */
const LONG_FACT = 60;
const factClass = (text: string | null | undefined): string =>
  (text ?? '').length > LONG_FACT ? 'portal-fact portal-fact-long' : 'portal-fact';

/** Inside a deal's own card the manifest title's "<deal name>: " prefix is noise; the Documents section keeps it. */
const cardDocLabel = (dealName: string | undefined, title: string): string =>
  dealName && title.startsWith(`${dealName}: `) ? title.slice(dealName.length + 2) : title;

/** Keys that are plumbing, not content. `tier` survives filtering; it is never displayed. */
const SKIP_KEYS = new Set(['tier', '_comment', '$schema']);

/*
  Arriving somewhere is not the same as SEEING that you arrived (VP8.2). Joseph, 2026-09-10:
  clicking a property on the map "goes to the deal but I can't even see which deal exactly" - the
  page scrolled a card to centre and left it looking exactly like its eleven neighbours.

  So a jump stamps the card for two seconds. `scrollIntoView` alone is not enough for a second
  reason too: a card inside a closed `<details>` has NO layout, so the browser scrolls to where the
  summary is and the card is never shown. Every collapsed ancestor is opened first.
*/
export const FOCUS_CLASS = 'portal-deal-focus';
export const FOCUS_MS = 2000;

/** Opens every collapsed `<details>` above an element. Returns how many it had to open. */
export const openCollapsedAncestors = (el: Element | null): number => {
  let opened = 0;
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    if (node instanceof HTMLDetailsElement && !node.open) {
      node.open = true;
      opened += 1;
    }
  }
  return opened;
};

/**
 * Renders an arbitrary object from the contract as label/value rows.
 *
 * Generic on purpose: `property.zoning`, `property.water` and friends differ deal to deal
 * (one has basins, another has a canal), and enumerating them would make the renderer
 * deal-specific - exactly what rule 2 forbids. Strings print verbatim, which is how the
 * confidence labels ("REPORTED", "UNVERIFIED") reach the page.
 */
function FactList({ value, suffix }: { value: unknown; suffix?: string }) {
  if (value === null || typeof value !== 'object') return null;
  const entries = Object.entries(value as Record<string, unknown>).filter(
    ([k, v]) => !SKIP_KEYS.has(k) && v !== null && v !== undefined && v !== ''
  );
  if (!entries.length) return null;

  return (
    <dl className="portal-facts">
      {entries.map(([k, v]) => {
        if (Array.isArray(v)) {
          const flat = v
            .map((item) =>
              item !== null && typeof item === 'object'
                ? Object.entries(item as Record<string, unknown>)
                    .filter(([ik]) => !SKIP_KEYS.has(ik))
                    .map(([ik, iv]) => scalarText(ik, iv))
                    .filter(Boolean)
                    .join(' — ')
                : scalarText(k, item)
            )
            .filter(Boolean);
          if (!flat.length) return null;
          return (
            <div className="portal-fact" key={k}>
              <dt>{humanize(k)}</dt>
              <dd>{flat.join(' · ')}</dd>
            </div>
          );
        }
        if (v !== null && typeof v === 'object') {
          return (
            <div className="portal-fact" key={k}>
              <dt>{humanize(k)}</dt>
              <dd>
                {Object.entries(v as Record<string, unknown>)
                  .filter(([ik]) => !SKIP_KEYS.has(ik))
                  .map(([ik, iv]) => scalarText(ik, iv))
                  .filter(Boolean)
                  .join(' · ')}
              </dd>
            </div>
          );
        }
        const text = scalarText(k, v);
        if (text === null) return null;
        return (
          <div className={factClass(text)} key={k}>
            <dt>{humanize(k)}</dt>
            <dd>
              {text}
              {suffix ?? ''}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

/* ─────────────────────────────────── parcel table ──────────────────────────────────── */

type SortKey = 'apn' | 'owner' | 'acres';

function ParcelTable({
  parcels,
  showAssessed,
  showTax,
  sortKey,
  sortDir,
  onSort,
  renderNotes,
  selectedApn,
  onLocate,
}: {
  parcels: PortalParcel[];
  showAssessed: boolean;
  showTax: boolean;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  /** Optional per-row slot. Absent (buyer/public links) means no extra column at all. */
  renderNotes?: (apn: string) => ReactNode;
  /** Map sync (Task 11), both optional: with no map on the page there is no locate column. */
  selectedApn?: string | null;
  onLocate?: (apn: string) => void;
}) {
  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...parcels].sort((a, b) => {
      if (sortKey === 'acres') return ((a.acres ?? 0) - (b.acres ?? 0)) * dir;
      const av = String(a[sortKey] ?? '');
      const bv = String(b[sortKey] ?? '');
      return av.localeCompare(bv) * dir;
    });
  }, [parcels, sortKey, sortDir]);

  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  return (
    <div className="portal-table-scroll">
      <table className="portal-table">
        <thead>
          <tr>
            {/*
              Carries the same class as the body cells so ONE print rule hides the whole
              column: hiding only the cells leaves the header row one wider than every body
              row, which shifts every heading a column to the left on paper.
            */}
            {onLocate && <th scope="col" className="portal-locate-cell" aria-label="Locate" />}
            <th scope="col">
              <button type="button" onClick={() => onSort('apn')}>
                APN{arrow('apn')}
              </button>
            </th>
            <th scope="col">County</th>
            <th scope="col" className="portal-num">
              <button type="button" onClick={() => onSort('acres')}>
                Acres{arrow('acres')}
              </button>
            </th>
            <th scope="col">
              <button type="button" onClick={() => onSort('owner')}>
                Owner{arrow('owner')}
              </button>
            </th>
            <th scope="col">Use</th>
            {showAssessed && (
              <th scope="col" className="portal-num">
                Assessed value
              </th>
            )}
            {showTax && <th scope="col">Tax status</th>}
            {renderNotes && <th scope="col" aria-label="Notes" />}
          </tr>
        </thead>
        <tbody>
          {sorted.map((p, i) => (
            <tr
              key={p.apn ?? i}
              id={p.apn ? `parcel-${p.apn}` : undefined}
              className={p.apn && p.apn === selectedApn ? 'portal-row-selected' : undefined}
            >
              {onLocate && (
                <td className="portal-locate-cell">
                  <button
                    type="button"
                    aria-label={`Show ${p.apn} on map`}
                    onClick={() => p.apn && onLocate(p.apn)}
                  >
                    ⌖
                  </button>
                </td>
              )}
              {/*
                The APN is its own node so the anchor marker cannot split the text - a cell
                rendered as two adjacent text nodes is invisible to an exact text lookup, and
                the APN is the one string a reader (or a test) searches this table by.
              */}
              <td>
                <span>{p.apn ?? '—'}</span>
                {p.isAnchor && <span title="Anchor parcel"> ★</span>}
              </td>
              <td>{p.county ?? '—'}</td>
              <td className="portal-num">
                {typeof p.acres === 'number' ? decimal(p.acres) : '—'}
              </td>
              <td>{p.owner ?? '—'}</td>
              <td>{p.use ?? '—'}</td>
              {showAssessed && (
                <td className="portal-num">{money(p.assessedValue) ?? '—'}</td>
              )}
              {showTax && <td>{p.taxStatus?.value ?? '—'}</td>}
              {/*
                Guarded on the APN itself: a parcel with no APN has no valid anchor, and the
                server's allowlist is the APNs the deal carries. Rendering a chip that can
                only ever 400 is worse than rendering nothing.
              */}
              {renderNotes && (
                <td className="portal-note-cell">{p.apn ? renderNotes(p.apn) : null}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ──────────────────────────────────── the page ─────────────────────────────────────── */

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/** Plain href: the API 302s to a short-lived signed URL, so no JS is needed to download. */
export const portalDocumentUrl = (token: string, docId: string): string =>
  `${API_BASE}/portal/${token}/documents/${docId}`;

/** A deal's picture: the OM's cover, cropped. Served only through this portal's own token. */
export const portalCoverUrl = (token: string, dealId: string): string =>
  `${API_BASE}/portal/${token}/cover/${dealId}`;

/*
  BUY1 - THE TABS. A page has tabs only when its contract declares the property-then-buying pair (`tabsOf`) AND carries a
  buy search. Every other portal - Vin's, Carlos's, Riley's, the area pages, an owner page without a search - gets null
  here and renders exactly as it always has: no tab bar, no panel, nothing below reads a tab.
*/
const pageTabsOf = (deal: PortalDeal | undefined): PortalTab[] | null =>
  deal && pipelineKindOf(deal.pipeline) === 'buy-search' ? tabsOf(deal) : null;

/** The buy search's buildings: a `#deal-<id>` link naming one of them opens the Buying tab. */
const buyingDealIdsOf = (deal: PortalDeal | undefined): Set<string> => new Set((deal?.pipeline?.deals ?? []).map((d) => d.id));

export default function ClientPortal() {
  const { token } = useParams<{ token: string }>();
  const [portal, setPortal] = useState<PortalPayload | null>(null);
  // Notes live in their own state, not inside `portal`: an accepted write appends here so the
  // thread updates without a refetch, while the rest of the payload stays exactly as served.
  const [notes, setNotes] = useState<NonNullable<PortalPayload['notes']>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // TP-RL2: refused for too many visits (a 429) - the link is fine, so this is never shown as a dead one.
  const [rateLimited, setRateLimited] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('acres');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Map ⇄ table selection (Task 11). One selected APN, shared both ways; `focusRequest`
  // carries a sequence number so clicking the same row twice re-flies the map instead of
  // being swallowed as an unchanged prop.
  const [selectedApn, setSelectedApn] = useState<string | null>(null);
  const [focusRequest, setFocusRequest] = useState<{ apn: string; seq: number } | null>(null);
  // VP8.5: the popup's "Ask Apex" asks ONE card's note thread to open; the seq makes a repeat click count.
  const [askRequest, setAskRequest] = useState<{ dealId: string; seq: number } | null>(null);

  /*
    BUY1 - the tabs, derived once per payload (null for every portal that declares none). `tabChoice` is what the reader
    or the link picked; `activeTab` is always one of the page's tabs, the property tab until something picks another.
    The buy search is handed to its tab as ONE reference per payload, so the tab's own memos hold across renders.
  */
  const fullDeal = portal?.deal;
  const tabs = useMemo(() => pageTabsOf(fullDeal), [fullDeal]);
  const buyingDealIds = useMemo(() => buyingDealIdsOf(fullDeal), [fullDeal]);
  const buySearch = useMemo(() => (tabs ? fullDeal?.pipeline ?? null : null), [tabs, fullDeal]);
  const [tabChoice, setTabChoice] = useState<PortalTabKey | null>(null);
  const activeTab: PortalTabKey | null = tabs ? tabs.find((t) => t.key === tabChoice)?.key ?? tabs[0].key : null;

  /*
    A link followed while the page is open (a building's `#deal-<id>`, `#buying`, an owner section) moves to its tab; a
    hash that names neither tab leaves the open one alone. The FIRST tab is decided where the payload arrives (below), so
    the first paint is already the tab the link asked for. The hash is read here, never rewritten: a building's own
    hash is what BuySearchTab follows to open its group and scroll to the card.
  */
  useEffect(() => {
    if (!tabs) return;
    const follow = () => {
      const key = tabForHash(window.location.hash, tabs, buyingDealIds);
      if (key) setTabChoice(key);
    };
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, [tabs, buyingDealIds]);

  /* A tab the reader picks goes into the address (a refresh keeps it, the link can be sent) without a history entry per
     tap - and replaceState fires no hashchange. The router's own history state is kept as it is. */
  const selectTab = (key: PortalTabKey) => {
    setTabChoice(key);
    window.history.replaceState(window.history.state, '', `#${key}`);
  };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setRateLimited(false);
    api
      .fetchPublicPortal(token)
      .then((res: { success?: boolean; portal?: PortalPayload; error?: string; rateLimited?: boolean }) => {
        if (res?.success && res.portal) {
          setPortal(res.portal);
          setNotes(res.portal.notes ?? []);
          // BUY1: the tab the link names, if the page has tabs (`#buying`, a building, a section); null otherwise.
          const arrived = res.portal.deal;
          setTabChoice(tabForHash(window.location.hash, pageTabsOf(arrived), buyingDealIdsOf(arrived)));
        } else if (res?.rateLimited) setRateLimited(true);
        else setError(res?.error || 'This link is invalid or has expired.');
      })
      // A network failure and a rejected token look the same to the person holding the link;
      // both get the same calm message rather than a blank page.
      .catch(() => setError('This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const onParcelClickFromMap = (apn: string) => {
    setSelectedApn(apn);
    /*
      A PARCEL click still travels to its table row, as it always has. Anything else - a pipeline
      PIN, or a polygon on a portal with no parcel table - selects and STAYS PUT: the popup opens
      where the reader is looking, and its "Go to deal" button is what travels. Scrolling on click
      carried the reader away from the one thing that names what they had just clicked.
    */
    document.getElementById(`parcel-${apn}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const goToDeal = (dealId: string) => {
    setSelectedApn(dealId);
    const card = document.getElementById(`deal-${dealId}`);
    if (!card) return;
    openCollapsedAncestors(card);
    card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    card.classList.add(FOCUS_CLASS);
    window.setTimeout(() => card.classList.remove(FOCUS_CLASS), FOCUS_MS);
  };
  /* VP8.5: the popup's "Ask Apex" - travel to the deal, then open its note form with the cursor in it. */
  const askAboutDeal = (dealId: string) => {
    goToDeal(dealId);
    setAskRequest((r) => ({ dealId, seq: (r?.seq ?? 0) + 1 }));
  };
  const onLocateFromTable = (apn: string) => {
    setSelectedApn(apn);
    setFocusRequest((f) => ({ apn, seq: (f?.seq ?? 0) + 1 }));
    document.getElementById('portal-map-slot')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  const onViewInTable = (apn: string) => {
    setSelectedApn(apn);
    document.getElementById(`parcel-${apn}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const toggleSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'acres' ? 'desc' : 'asc');
    }
  };

  if (loading) {
    return (
      <div className="portal">
        <div className="portal-state">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (rateLimited) {
    return (
      <div className="portal">
        <div className="portal-state">
          <div>
            <h1>Please wait a few minutes</h1>
            <p>This page has had too many visits in a short time.</p>
            <p>Your link still works - please try again in a few minutes.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !portal) {
    return (
      <div className="portal">
        <div className="portal-state">
          <div>
            <h1>This link is no longer active</h1>
            <p>{error || 'This link is invalid or has expired.'}</p>
            <p>Please contact your Apex broker for a current link.</p>
          </div>
        </div>
      </div>
    );
  }

  /*
    BUY1: everything below reads the PROJECTED deal. On the property tab that is the contract without its buy search, so
    the tab is the owner's page exactly as it was; on the Buying tab, and on every page without tabs, it is the contract.
  */
  const deal: PortalDeal = projectForTab(portal.deal ?? {}, activeTab);
  const onBuying = activeTab === 'buying' && buySearch !== null;
  const meta = deal.meta ?? {};
  const property = deal.property ?? {};
  const parcels = deal.parcels ?? [];
  const parcelGroups = deal.parcelGroups ?? {};
  const valuation = deal.valuation;
  const netProceeds = deal.netProceeds;
  // VP9, his answer 2026-09-10: a closed item drops off the page - and out of every count that reads this list.
  const openItems = (deal.openItems ?? []).filter((o) => !o.closedAt);
  const documents = portal.documents ?? [];
  const brokers = deal.brokers ?? [];
  const call = deal.meta?.callButton === true ? callTarget(brokers) : null;
  const features = portal.parcelsGeojson?.features ?? [];

  /*
    The pipeline (schemaVersion 1.2). Deals group by the contract's OWN legend order, never a
    hardcoded one; the map takes the same groups so the legend colours and the card badges agree
    by construction. A deal's documents resolve manifest id -> manifest title -> served row, so a
    card can only offer a download the server actually listed for this link's tier.
  */
  const pipelineDeals = deal.pipeline?.deals ?? [];
  // Sorted by the contract's explicit `order`, never by key order: the payload comes out of a jsonb
  // column, and Postgres re-orders jsonb keys by LENGTH (measured 2026-09-09: "Analysed" rendered
  // last of six). Groups without an order keep their arrival position after the ordered ones.
  const rawGroups = deal.pipeline?.groups ?? {};
  const pipelineGroupKeys = Object.keys(rawGroups).sort((a, b) => {
    const oa = typeof rawGroups[a]?.order === 'number' ? (rawGroups[a].order as number) : Number.MAX_SAFE_INTEGER;
    const ob = typeof rawGroups[b]?.order === 'number' ? (rawGroups[b].order as number) : Number.MAX_SAFE_INTEGER;
    return oa - ob;
  });
  const pipelineGroups: Record<string, PortalParcelGroup> = Object.fromEntries(
    pipelineGroupKeys.map((k) => [k, rawGroups[k]])
  );
  const dealsByGroup = pipelineGroupKeys
    .map((key) => ({ key, group: pipelineGroups[key], rows: pipelineDeals.filter((d) => d.status?.group === key) }))
    .filter((g) => g.rows.length > 0);
  const ungroupedDeals = pipelineDeals.filter((d) => !d.status?.group || !pipelineGroupKeys.includes(d.status.group));
  const manifestById = new Map((deal.documents ?? []).filter((m) => m.id).map((m) => [m.id as string, m]));
  const rowByTitle = new Map(documents.map((r) => [r.title ?? '', r]));
  const docsForDeal = (d: PortalPipelineDeal) =>
    (d.documents ?? [])
      .map((id) => {
        const m = manifestById.get(id);
        if (!m) return null;
        return { id, title: m.title ?? id, category: m.category ?? null, row: rowByTitle.get(m.title ?? '') };
      })
      .filter((x): x is { id: string; title: string; category: string | null; row: PortalDocument | undefined } => x !== null);
  const dealNameById = new Map(pipelineDeals.map((d) => [d.id, d.name ?? d.id]));
  /*
    The terms table (VP8.4) reads the deals in the order the cards do - by the legend, then as the
    contract lists them - and names the analysis a row quotes only when the deal HAS more than one:
    one analysis needs no label, and three with no label would leave the reader guessing which set of
    figures the row is.
  */
  const termsDeals: TermsRowDeal[] = [...dealsByGroup.flatMap((g) => g.rows), ...ungroupedDeals];
  const termsSourceNote = (d: TermsRowDeal): string | null => {
    const src = d.terms?.source;
    if (!src) return null;
    const analyses = (d.documents ?? []).filter((id) => manifestById.get(id)?.category === 'Investment Analysis');
    if (analyses.length < 2) return null;
    const title = manifestById.get(src)?.title;
    if (!title) return null;
    const prefix = `${d.name ?? ''}: `;
    return title.startsWith(prefix) ? title.slice(prefix.length) : title;
  };
  const timelineItems = deal.timeline?.items ?? [];
  const buyBox = deal.buyBox;
  // Index-signature reads do not narrow inside JSX, so the two typed reads happen here.
  const buyBoxFloors = buyBox && buyBox.floors !== null && typeof buyBox.floors === 'object' ? buyBox.floors : null;
  const buyBoxSource = typeof buyBox?.source === 'string' ? buyBox.source : null;
  /*
    The 1031 clock (VP8.1). It lives ON the buy box in the contract - it is a fact about the money,
    not about any one deal - and it is PRINTED at the top, because the two deadlines are what a 1031
    buyer reads the page for. Absent for any other kind of portal, and the section then never mounts.
  */
  const exchange =
    buyBox && buyBox.exchange !== null && typeof buyBox.exchange === 'object'
      ? (buyBox.exchange as PortalExchange)
      : null;
  const clockRows = exchangeRows(exchange, todayIso());
  const mapGroups = pipelineGroupKeys.length > 0 ? pipelineGroups : parcelGroups;
  const offersInPlay = pipelineDeals.filter((d) => d.status?.group === 'active').length;
  /*
    Schema 1.3: a tenant's lease search reads a different page out of the same renderer. The KIND picks the words
    and the order; nothing below asks which client this is. A contract with no kind is a buyer's, exactly as before.
  */
  const kind = pipelineKindOf(deal.pipeline);
  // The lease-search-only pieces of THIS body (its section order, phone menu, lead section, at-a-glance table).
  const leaseSearch = kind === 'lease-search';
  // BUY1: a building's card is a spec scorecard on a tenant's search AND a buyer's search (the Buying tab's cards are
  // drawn by renderDealCard too). Read off the contract itself, so the projection cannot change a card.
  const specSearchCards = isSpecSearch(pipelineKindOf(portal.deal?.pipeline));
  // The cards' own words (the tally's "checking" on a buy search), read off the same contract as `specSearchCards`.
  const cardWords = KIND_WORDS[pipelineKindOf(portal.deal?.pipeline)];
  const words = KIND_WORDS[kind];
  const scorecardRows: ScorecardRowDef[] = (deal.pipeline?.scorecard?.rows ?? []).filter((r) => r && typeof r.key === 'string');
  // The best matches lead: the contract FLAGS the group (never its key's name, never "the first one").
  const leadKey = leaseSearch ? pipelineGroupKeys.find((k) => rawGroups[k]?.lead === true) ?? null : null;
  const leadGroup = leadKey ? dealsByGroup.find((g) => g.key === leadKey) ?? null : null;
  const restGroups = leadGroup ? dealsByGroup.filter((g) => g.key !== leadKey) : dealsByGroup;
  // A deal whose group the legend does not name still shows, in a trailing "Other" bucket - never vanishes.
  const otherBucket: Array<{ key: string; group: PortalParcelGroup; rows: PortalPipelineDeal[] }> = ungroupedDeals.length
    ? [{ key: '', group: { label: 'Other' }, rows: ungroupedDeals }]
    : [];
  // The groups drawn below the map, in order. The Buildings section and the phone menu both read THIS list.
  const restBuckets = [...restGroups, ...otherBucket];
  // Every building, a group at a time, for the at-a-glance table and the phone's one-line list.
  const glanceGroups: GlanceGroup[] = [...dealsByGroup, ...otherBucket].map(({ key, group, rows }) => ({
    key,
    label: groupLabel(key, group),
    color: group?.mapColor,
    rows,
  }));
  // His requirement as ordered rows: an object cannot keep his order (jsonb sorts keys by length).
  const buyBoxRows = Array.isArray(buyBox?.rows) ? (buyBox.rows as LeaseFact[]).filter((r) => r?.label && r?.value) : null;
  const market = deal.marketContext;
  const marketRows = (market?.rows ?? []).filter((r) => r?.area && r?.directAsking);
  // A column only when some row fills it: Carlos's analysis has neither, and an empty column reads as missing data.
  const marketHasSegment = marketRows.some((r) => r.segment);
  const marketHasTypical = marketRows.some((r) => r.typical);
  const marketTakeaways = (Array.isArray(market?.takeaways) ? market.takeaways : []).filter((t) => typeof t === 'string' && t.trim());
  const loiCount = (deal.documents ?? []).filter((m) => m.category === 'Letter of Intent').length;
  const analysisCount = (deal.documents ?? []).filter((m) => m.category === 'Investment Analysis').length;

  const showAssessed = parcels.some((p) => typeof p.assessedValue === 'number');
  const showTax = parcels.some((p) => !!p.taxStatus?.value);
  // No geometry, no locate column. A ⌖ button with no map to move is a dead control, and a
  // portal whose parcels have not been fetched yet still has to look finished.
  const locateOnMap = features.length > 0 ? onLocateFromTable : undefined;

  // Group keys come from the data, not a hardcoded pair: a deal with three groups (or one)
  // renders correctly. Parcels whose group is unknown fall into a trailing "Other" bucket
  // rather than vanishing from the table.
  const groupKeys = Object.keys(parcelGroups);
  const grouped = groupKeys
    .map((key) => ({ key, group: parcelGroups[key], rows: parcels.filter((p) => p.group === key) }))
    .filter((g) => g.rows.length > 0);
  const ungrouped = parcels.filter((p) => !p.group || !groupKeys.includes(p.group));

  const subtitleParts = [meta.dealType, meta.status, meta.asOfDate ? `As of ${meta.asOfDate}` : null]
    .filter(Boolean)
    .join(' · ');

  // Property sub-blocks are whatever the contract carries, minus the headline (rendered as
  // the lead) and any scalar strings (shown as a single block).
  const propertyBlocks = Object.entries(property).filter(
    ([k, v]) => k !== 'headline' && !SKIP_KEYS.has(k) && v !== null && typeof v === 'object'
  );

  /*
    Notes (v2 design §3). Two conditions gate every chip on this page, and both live here:
    `canWrite` (only a family link may POST) and the note count. A buyer or public link is
    served no `notes` key at all, so it gets neither a thread nor a form - PortalNotes
    returns null on that combination. Anchors are only ever composed from keys the server's
    allowlist accepts: the eight section keys, an APN the deal carries, a document id from
    this payload, a comp id from this payload.
  */
  const canWrite = portal.tier === 'family';
  /*
    VP8.5 - "new since your last visit". The server says when this link's previous visit ended (null on a
    first visit, so nothing is marked); a card is NEW when its deal reached the portal after that, UPDATED
    when its content or one of its documents did.
  */
  const since = portal.lastVisitAt ?? null;
  const markByDeal = new Map(pipelineDeals.map((d) => [d.id, dealMark(d, docsForDeal(d).map((x) => x.row), since)]));
  const marks = [...markByDeal.values()];
  // BUY1 (final review): on the Buying tab the line is about its buildings alone - no documents (they belong to the
  // property tab, where nothing on Buying would be marked) and "building", not "deal". Every other page as before.
  const sinceSummary = since
    ? sinceLine(
        {
          newDeals: marks.filter((m) => m === 'new').length,
          updatedDeals: marks.filter((m) => m === 'updated').length,
          newDocs: onBuying ? 0 : (portal.documents ?? []).filter((doc) => docIsNew(doc, since)).length,
        },
        since,
        onBuying ? { one: 'building', many: 'buildings' } : undefined
      )
    : null;

  const notesFor = (anchorType: string, anchorKey: string) =>
    notes
      .filter((n) => n.anchorType === anchorType && n.anchorKey === anchorKey)
      .map(({ id, authorName, body, createdAt }) => ({ id, authorName, body, createdAt }));

  const submitNote =
    (anchorType: string, anchorKey: string) =>
    async (authorName: string, body: string): Promise<boolean> => {
      try {
        // Annotated at the call site, like fetchPublicPortal's: `api` hands back a parsed
        // JSON `any`, and an unannotated `res.note` would flow into state untyped.
        const res: { success?: boolean; note?: PortalNoteRow; error?: string } =
          await api.createPortalNote(token ?? '', {
            anchorType,
            anchorKey,
            authorName,
            body,
          });
        // Append ONLY on a real 201-with-note. A rejected write (403, 400 anchor, 429 cap)
        // must not leave a note on screen that the server never stored.
        if (res?.success && res.note) {
          // Bound to a local: the narrowing of `res.note` does not survive into the updater
          // closure, which TypeScript must assume runs later.
          const stored = res.note;
          setNotes((prev) => [...prev, stored]);
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };

  const renderParcelNotes = (apn: string) => (
    <PortalNotes
      compact
      notes={notesFor('parcel', apn)}
      canWrite={canWrite}
      onSubmit={submitNote('parcel', apn)}
    />
  );

  /*
    §3 to the letter: "buyer/public links render no notes UI at all - the affordance itself
    must not appear". PortalNotes returning null is not enough for the TABLE render props -
    merely PASSING them mints an empty Notes columnheader and an empty trailing cell in every
    row, so the three render props below are gated on `canWrite` rather than passed always.
    Safe today because a family link is the only tier that both writes and reads notes; if a
    read-only-notes tier is ever added this gate moves SERVER-SIDE, not to a second flag.
  */

  /*
    The jump menu (v2.1 §1). Its entries are derived from the SAME conditions the sections
    below render on, and that derivation lives HERE rather than inside PortalSectionNav: a
    hardcoded list in the nav would advertise a Proceeds chip to a buyer link whose payload
    carries no netProceeds key at all, and scroll them to nothing. Keys are the note section
    anchors; the label is what a reader sees, so "netProceeds" reads as "Proceeds".
  */
  const hasComps = (deal.comps?.groups ?? []).some((g) => (g.items ?? []).length > 0);
  const navEntries = [
    { key: 'overview', label: 'Overview', show: true },
    // Joseph, 2026-09-10: open items closer to the top. They and the clock are what is LIVE; the
    // criteria and the archive are reference. The nav is derived from these same rows, so moving a
    // section here moves its chip too - which is why the list is ordered, not sorted.
    { key: 'exchange', label: 'Exchange clock', show: clockRows.length > 0 },
    // A tenant reads his requirement and the best matches first; a buyer reads what is live first. The sections
    // below render in this same order, by the same test.
    ...(leaseSearch
      ? [
          { key: 'criteria', label: words.criteria, show: Boolean(buyBox) },
          { key: 'lead', label: leadGroup?.group?.label || 'Best matches', show: Boolean(leadGroup) },
          { key: 'openItems', label: 'Open items', show: openItems.length > 0 },
        ]
      : [
          { key: 'openItems', label: 'Open items', show: openItems.length > 0 },
          { key: 'criteria', label: words.criteria, show: Boolean(buyBox) },
        ]),
    { key: 'map', label: 'Map', show: features.length > 0 },
    // VP8.4: every deal on one line, read before the cards it summarises.
    { key: 'terms', label: words.termsNav, show: pipelineDeals.length > 0 },
    { key: 'deals', label: words.deals, show: restGroups.length + ungroupedDeals.length > 0 },
    { key: 'parcels', label: 'Parcels', show: parcels.length > 0 },
    { key: 'valuation', label: 'Valuation', show: Boolean(valuation) },
    { key: 'comps', label: 'Comps', show: hasComps },
    { key: 'netProceeds', label: 'Proceeds', show: Boolean(netProceeds) },
    { key: 'market', label: market?.title ? 'Rental analysis' : 'Asking rents', show: marketRows.length > 0 },
    { key: 'timeline', label: 'Timeline', show: timelineItems.length > 0 },
    { key: 'documents', label: 'Documents', show: documents.length > 0 },
  ]
    .filter((e) => e.show)
    .map(({ key, label }) => ({ key, label }));

  /*
    BUY1: the jump menu and the print chooser list the OPEN tab's sections and nothing else. On the Buying tab that is the
    tab's own list, built from the same conditions that draw its sections (so a chip never jumps to the other tab); on
    the property tab, and on every page without tabs, it is the list above.
  */
  const sectionEntries = onBuying && buySearch ? buySearchNavEntries(buySearch, portal.deal?.buyBox) : navEntries;

  /*
    The phone menu (TP7, his ask 2026-10-13) - on a tenant's page only, so Vin's keeps its row. A section gets a count
    only where the page already prints that same number for it (the best matches' heading, the overview's open items
    and buildings); "Buildings" gets none, because the overview says 47 and that section holds 43 - the best matches
    sit above it - and two numbers for one thing read as a mistake. Its groups are listed beneath it, each with the
    label and count its own heading prints.
  */
  const phoneMenu: PortalPhoneMenu | undefined = leaseSearch
    ? {
        counts: {
          ...(leadGroup ? { lead: leadGroup.rows.length } : {}),
          openItems: openItems.length,
          terms: pipelineDeals.length,
        },
        subs: {
          deals: restBuckets.map(({ key, group, rows }) => ({
            id: groupElementId(key),
            label: groupLabel(key, group),
            count: rows.length,
          })),
        },
      }
    : undefined;

  const docGroups: Array<{ label: string; docs: PortalDocument[] }> = [];
  documents.forEach((d) => {
    // Pending rows carry "Category · PENDING — why"; the head groups, the tail is displayed
    // on the row itself so the reason survives verbatim.
    const label = (d.category ?? '').split(' · ')[0] || 'Documents';
    const existing = docGroups.find((g) => g.label === label);
    if (existing) existing.docs.push(d);
    else docGroups.push({ label, docs: [d] });
  });

  /*
    One card, drawn by ONE function - the lead section above the map and the groups below it must not be able to
    differ (a fix made in one caller is a defect left in the other).
  */
  const renderDealCard = (d: PortalPipelineDeal, group: PortalParcelGroup | undefined) => {
                    const docs = docsForDeal(d);
                    const mark = markByDeal.get(d.id) ?? null;
                    // The seller's cap and ours, side by side - the same two figures the terms table shows.
                    const omCap = omCapText(d.omCap, d.ask);
                    const ourCap = ourCapText(d.terms?.ourCap, d.ask);
                    const color = group?.mapColor || 'var(--apex-line)';
                    return (
                      <article className="portal-deal" id={`deal-${d.id}`} key={d.id} data-testid={`portal-deal-${d.id}`}>
                        {/*
                          The picture is the OM's own cover page, cropped to the photograph. It leads
                          the card because recognition is its whole job - Vin has seen these images
                          attached to Joseph's emails. `onError` hides it rather than leaving a
                          broken-image frame: a signed URL can expire mid-read, and 23 deals have no
                          OM to cut a cover from at all.
                        */}
                        {d.hasCover && (
                          <img
                            className="portal-deal-cover"
                            src={portalCoverUrl(token ?? '', d.id)}
                            alt=""
                            loading="lazy"
                            data-testid={`portal-cover-${d.id}`}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="portal-deal-head">
                          <h4>
                            {d.mapLetter && <MapLetter letter={d.mapLetter} color={color} />}
                            {d.name ?? d.id}
                            {mark && (
                              <span className={`portal-since-badge portal-since-${mark}`}>{mark === 'new' ? 'New' : 'Updated'}</span>
                            )}
                          </h4>
                          {d.status?.label && (
                            <span className="portal-badge portal-deal-status" style={{ borderColor: color, color }}>
                              {d.status.label}
                            </span>
                          )}
                        </div>
                        <p className="portal-deal-sub">
                          {[d.tenant, [d.address, d.suite, d.city].filter(Boolean).join(', '), d.propertyType].filter(Boolean).join(' · ')}
                        </p>
                        {specSearchCards ? (
                          <LeaseSearchCardBody deal={d} rows={scorecardRows} unknownTally={cardWords.unknownTally} />
                        ) : (
                        <dl className="portal-facts">
                          {d.ask && (
                            <div className="portal-fact">
                              <dt>Asking</dt>
                              <dd>{d.ask}</dd>
                            </div>
                          )}
                          {omCap && (
                            <div className="portal-fact">
                              <dt>OM cap</dt>
                              <dd>
                                <CapText parts={omCapParts(d.omCap, d.ask)} />
                              </dd>
                            </div>
                          )}
                          {ourCap && (
                            <div className="portal-fact">
                              <dt>Our cap</dt>
                              <dd>
                                <CapText parts={ourCapParts(d.terms?.ourCap, d.ask)} />
                              </dd>
                            </div>
                          )}
                          {!omCap && !ourCap && d.cap && (
                            <div className="portal-fact">
                              <dt>Cap rate</dt>
                              <dd>{d.cap}</dd>
                            </div>
                          )}
                          {d.verdict && (
                            <div className={factClass(d.verdict)}>
                              <dt>Our read</dt>
                              <dd>
                                {d.verdict}
                                {d.verdictSource && <span className="portal-deal-source"> ({d.verdictSource})</span>}
                              </dd>
                            </div>
                          )}
                          {d.analysisDate && (
                            <div className="portal-fact">
                              <dt>Analysis</dt>
                              <dd>{d.analysisDate}</dd>
                            </div>
                          )}
                          {d.status?.label && (
                            <div className={factClass(d.status.label)}>
                              <dt>Where this stands</dt>
                              <dd>
                                {d.status.label}
                                {d.status.asOf && <span className="portal-deal-source"> (as of {d.status.asOf})</span>}
                                {(d.status.evidence ?? d.status.audit?.evidence) && (
                                  <details className="portal-deal-evidence">
                                    <summary>Evidence</summary>
                                    <p>{d.status.evidence ?? d.status.audit?.evidence}</p>
                                  </details>
                                )}
                              </dd>
                            </div>
                          )}
                        </dl>
                        )}
                        {(d.loiHistory ?? []).length > 0 && (
                          <ol className="portal-deal-history">
                            {(d.loiHistory ?? []).map((h, i) => (
                              <li key={`${h.date ?? ''}-${i}`}>
                                <span className="portal-deal-history-date">{h.date}</span> {h.event}
                                {h.by && <span className="portal-deal-source"> ({h.by})</span>}
                              </li>
                            ))}
                          </ol>
                        )}
                        {d.sentToVin && <p className="portal-note">{d.sentToVin}</p>}
                        {d.omHeld && <p className="portal-note">{d.omHeld}</p>}
                        {docs.length > 0 && (
                          <ul className="portal-deal-docs">
                            {docs.map((x) => (
                              <li key={x.id}>
                                {x.row && x.row.status === 'available' ? (
                                  <>
                                  <a
                                    className="portal-download portal-deal-doc"
                                    href={portalDocumentUrl(token ?? '', x.row.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {cardDocLabel(d.name, x.title)}
                                  </a>
                                  {docIsNew(x.row, since) && <span className="portal-since-badge">New</span>}
                                  </>
                                ) : (
                                  <span className="portal-deal-doc">
                                    {cardDocLabel(d.name, x.title)} <span className="portal-badge">Pending</span>
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                        <PortalNotes
                          compact
                          notes={notesFor('deal', d.id)}
                          canWrite={canWrite}
                          onSubmit={submitNote('deal', d.id)}
                          openRequest={askRequest?.dealId === d.id ? askRequest.seq : undefined}
                        />
                      </article>
                    );
  };

  const openItemsSection = openItems.length > 0 && (
          <section className="portal-section" id="section-openItems">
            <h2>Open items</h2>
            <PortalNotes
              notes={notesFor('section', 'openItems')}
              canWrite={canWrite}
              onSubmit={submitNote('section', 'openItems')}
            />
            {openItems.map((o, i) => {
              /*
                His ask, 2026-09-10: a note per open item, "just so Vin could be like, it's for
                this". The thread is anchored to the item's own id, never its index - closing one
                item would otherwise re-point every note below it at the wrong item. An item with
                no id (an older contract) simply renders without a thread rather than posting to
                an anchor the server will refuse.
              */
              const anchor = typeof o.id === 'string' && o.id ? o.id : null;
              const dealId = typeof o.dealId === 'string' && o.dealId ? o.dealId : null;
              const card = dealId && pipelineDeals.some((d) => d.id === dealId) ? dealId : null;
              // VP9: the mark sits on the item itself - New since the last visit, or Updated when its words changed.
              const itemMark = dealMark(o, [], since);
              return (
                <div className="portal-open-item" key={anchor ?? `${o.item ?? 'item'}-${i}`}>
                  <p>
                    {o.item}
                    {itemMark && (
                      <span className={`portal-since-badge portal-since-${itemMark}`}>{itemMark === 'new' ? 'New' : 'Updated'}</span>
                    )}
                  </p>
                  {o.why && <p className="portal-open-why">{o.why}</p>}
                  <p className="portal-open-foot">
                    {o.owner && (
                      <span className="portal-open-owner">
                        {words.openItemOwner}: {o.owner}
                      </span>
                    )}
                    {card && (
                      <button type="button" className="portal-open-jump" onClick={() => goToDeal(card)}>
                        {words.viewDeal}
                      </button>
                    )}
                  </p>
                  {anchor && (
                    <PortalNotes
                      compact
                      addLabel="+ Note on this item"
                      notes={notesFor('openItem', anchor)}
                      canWrite={canWrite}
                      onSubmit={submitNote('openItem', anchor)}
                    />
                  )}
                </div>
              );
            })}
          </section>
  );

  const criteriaSection = buyBox && (
          <section className="portal-section" id="section-criteria">
            <h2>{words.criteria}</h2>
            <PortalNotes
              notes={notesFor('section', 'criteria')}
              canWrite={canWrite}
              onSubmit={submitNote('section', 'criteria')}
            />
            <div className="portal-fact-block">
              {buyBoxRows ? (
                /* Schema 1.3: the requirement as the client stated it, line for line and in his order. */
                <dl className="portal-facts">
                  {buyBoxRows.map((r) => (
                    <div className={factClass(r.value)} key={r.label}>
                      <dt>{r.label}</dt>
                      <dd>{r.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                /* `exchange` has its own section at the top; `floors` and `source` have their own
                   treatment below. FactList prints whatever is left. */
                <FactList value={{ ...buyBox, floors: undefined, source: undefined, exchange: undefined }} />
              )}
            </div>
            {buyBoxFloors && (
              <>
                <h3>Floors every analysis is scored against</h3>
                <div className="portal-fact-block">
                  <FactList value={buyBoxFloors} />
                </div>
              </>
            )}
            {buyBoxSource && <p className="portal-note">Source: {buyBoxSource}</p>}
          </section>
  );

  /* Schema 1.3: the best matches, as their own section above the map - the group the contract flags as the lead. */
  const leadSection = leadGroup && (
    <section className="portal-section" id="section-lead">
      <h2>
        {leadGroup.group?.label || 'Best matches'} <span className="portal-group-count">({leadGroup.rows.length})</span>
      </h2>
      <PortalNotes notes={notesFor('section', 'lead')} canWrite={canWrite} onSubmit={submitNote('section', 'lead')} />
      {leadGroup.group?.description && <p className="portal-group-desc">{leadGroup.group.description}</p>}
      <div className="portal-deal-list portal-lead-list">{leadGroup.rows.map((d) => renderDealCard(d, leadGroup.group))}</div>
    </section>
  );

  /*
    BUY1 - the Buying tab: the buy search's own sections, each card drawn by THIS page's renderDealCard and each section's
    note thread the same PortalNotes the page mounts under every other heading. None of the owner's sections mount here.
  */
  const buyingBody = onBuying && buySearch && (
    <BuySearchTab
      pipeline={buySearch}
      buyBox={portal.deal?.buyBox}
      renderCard={renderDealCard}
      sectionNotes={(key) => (
        <PortalNotes notes={notesFor('section', key)} canWrite={canWrite} onSubmit={submitNote('section', key)} />
      )}
    />
  );

  /* The open tab's body, in the panel its tab names. A page without tabs gets its body as it always has - no wrapper. */
  const inTabPanel = (body: ReactNode): ReactNode =>
    activeTab ? (
      <div className="portal-tabpanel" role="tabpanel" id={portalTabPanelId(activeTab)} aria-labelledby={portalTabId(activeTab)}>
        {body}
      </div>
    ) : (
      body
    );

  return (
    <div className="portal">
      <div className="portal-shell">
        <header>
          <div className="portal-logos">
            <img src="/apex-logo.png" alt="Apex Real Estate Services" />
            <img src="/kw-commercial-logo.jpeg" alt="KW Commercial" />
          </div>
          <hr className="portal-rule" />
          <h1 className="portal-title">{meta.dealName || portal.title || 'Deal portal'}</h1>
          {subtitleParts && <p className="portal-subtitle">{subtitleParts}</p>}
          {meta.preparedBy && <p className="portal-subtitle">Prepared by {meta.preparedBy}</p>}
          {/* The handout: pick the sections, or the short one (his ask, 2026-09-10). Built from the SAME list as the
              jump menu, so a section cannot be in one and not the other. None of it prints. */}
          <div className="portal-header-actions portal-no-print">
            {/* BUY1: one chooser per tab (the key), so its pick is the open tab's own; Buying remembers its pick apart
                from the property's. Without tabs the key is absent and the slug is the portal's, as before. */}
            <PrintChooser
              key={activeTab ?? undefined}
              sections={sectionEntries}
              slug={onBuying ? `${portal.slug ?? 'portal'}:buying` : portal.slug}
              shortKeys={words.print?.shortKeys}
              termsHint={words.print?.termsHint}
            />
          </div>
          {sinceSummary && (
            <p className="portal-since portal-no-print" data-testid="portal-since">
              {sinceSummary} Marked below.
            </p>
          )}
        </header>

        {/* BUY1: drawn only for a contract that declares its tabs (`pageTabsOf`); every other portal has none. */}
        {tabs && activeTab && <PortalTabBar tabs={tabs} active={activeTab} onSelect={selectTab} />}

        <PortalSectionNav entries={sectionEntries} phoneMenu={phoneMenu} />

        {/* The open tab's body: the Buying tab's own sections, or the page as it always was. */}
        {inTabPanel(buyingBody || (
          <>
            {/* ── Overview ── */}
            <section className="portal-section" id="section-overview">
              <h2>Overview</h2>
              <PortalNotes
                notes={notesFor('section', 'overview')}
                canWrite={canWrite}
                onSubmit={submitNote('section', 'overview')}
              />
              {property.headline && <p className="portal-headline">{property.headline}</p>}

              <dl className="portal-stats">
                {typeof (property.acreage as Record<string, unknown>)?.grandTotal === 'number' && (
                  <div className="portal-stat">
                    <dt>Total acreage</dt>
                    <dd>{decimal((property.acreage as Record<string, number>).grandTotal)}</dd>
                  </div>
                )}
                {Array.isArray(property.counties) && property.counties.length > 0 && (
                  <div className="portal-stat">
                    <dt>{property.counties.length > 1 ? 'Counties' : 'County'}</dt>
                    <dd>{(property.counties as string[]).join(' / ')}</dd>
                  </div>
                )}
                {parcels.length > 0 && (
                  <div className="portal-stat">
                    <dt>Parcels</dt>
                    <dd>{parcels.length}</dd>
                  </div>
                )}
                {money(valuation?.recommendedList) && (
                  <div className="portal-stat">
                    <dt>Recommended list</dt>
                    <dd>{money(valuation?.recommendedList)}</dd>
                  </div>
                )}
                {leaseSearch && pipelineDeals.length > 0 && (
                  <div className="portal-stat">
                    <dt>Buildings</dt>
                    <dd>{pipelineDeals.length}</dd>
                  </div>
                )}
                {leaseSearch && leadGroup && (
                  <div className="portal-stat">
                    <dt>{leadGroup.group?.label || 'Best matches'}</dt>
                    <dd>{leadGroup.rows.length}</dd>
                  </div>
                )}
                {pipelineDeals.length > 0 && !leaseSearch && (
                  <div className="portal-stat">
                    <dt>Properties reviewed</dt>
                    <dd>{pipelineDeals.filter((d) => (d.role ?? 'buyer') === 'buyer').length}</dd>
                  </div>
                )}
                {pipelineDeals.length > 0 && !leaseSearch && (
                  <div className="portal-stat">
                    <dt>Offers in play</dt>
                    <dd>{offersInPlay}</dd>
                  </div>
                )}
                {analysisCount > 0 && (
                  <div className="portal-stat">
                    <dt>Analyses</dt>
                    <dd>{analysisCount}</dd>
                  </div>
                )}
                {loiCount > 0 && (
                  <div className="portal-stat">
                    <dt>Letters of intent</dt>
                    <dd>{loiCount}</dd>
                  </div>
                )}
                {openItems.length > 0 && (
                  <div className="portal-stat">
                    <dt>Open items</dt>
                    <dd>{openItems.length}</dd>
                  </div>
                )}
              </dl>

              {(money(valuation?.recommendedList) || moneyRange(valuation?.realEstateLow, valuation?.realEstateHigh)) && (
                <div className="portal-callout">
                  <p className="portal-callout-label">
                    {money(valuation?.recommendedList) ? 'Recommended list price' : 'Real estate value range'}
                  </p>
                  <p className="portal-callout-value">
                    {money(valuation?.recommendedList) ??
                      moneyRange(valuation?.realEstateLow, valuation?.realEstateHigh)}
                  </p>
                  {money(valuation?.recommendedList) &&
                    moneyRange(valuation?.realEstateLow, valuation?.realEstateHigh) && (
                      <p className="portal-callout-sub">
                        Real estate range {moneyRange(valuation?.realEstateLow, valuation?.realEstateHigh)}
                      </p>
                    )}
                </div>
              )}

              {propertyBlocks.length > 0 && (
                <div className="portal-facts-2col portal-facts">
                  {propertyBlocks.map(([k, v]) => (
                    <div key={k}>
                      <h3>{humanize(k)}</h3>
                      <div className="portal-fact-block">
                        <FactList value={v} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ── The 1031 clock (VP8.1) ── */}
            {clockRows.length > 0 && (
              <section className="portal-section" id="section-exchange">
                <h2>{exchange?.label || 'Exchange clock'}</h2>
                <PortalNotes
                  notes={notesFor('section', 'exchange')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'exchange')}
                />
                <ol className="portal-clock">
                  {clockRows.map((r) => (
                    <li className={`portal-clock-step${r.past ? ' portal-clock-past' : ''}`} key={r.key}>
                      <p className="portal-clock-label">{r.label}</p>
                      <p className="portal-clock-date">{r.date}</p>
                      {r.countdown && <p className="portal-clock-countdown">{r.countdown}</p>}
                      {r.note && <p className="portal-clock-note">{r.note}</p>}
                    </li>
                  ))}
                </ol>
                {exchange?.basis && <p className="portal-note">{exchange.basis}</p>}
              </section>
            )}

            {/* ── The requirement, the best matches and the open items - in the order the KIND reads them ── */}
            {leaseSearch ? (
              <>
                {criteriaSection}
                {leadSection}
                {openItemsSection}
              </>
            ) : (
              <>
                {openItemsSection}
                {criteriaSection}
              </>
            )}

            {/* ── Map (parcels, or pipeline pins) ── */}
            {features.length > 0 && (
              <section className="portal-section" id="section-map">
                {/* "Parcel map" only when the map draws a parcel roster; pins alone (a pipeline, or an owner's
                    comps around his building) are a plain map. */}
                <h2>{pipelineDeals.length > 0 || parcels.length === 0 ? 'Map' : 'Parcel map'}</h2>
                <PortalNotes
                  notes={notesFor('section', 'map')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'map')}
                />
                {/*
                  The map mounts only when geometry actually exists. A portal whose parcels have
                  not been fetched yet shows no map at all rather than an empty grey box that
                  reads as a broken page.
                */}
                <div id="portal-map-slot">
                  <PortalParcelMap
                    geojson={portal.parcelsGeojson as ParcelFeatureCollection}
                    groups={mapGroups}
                    selectedApn={selectedApn}
                    focusRequest={focusRequest}
                    onParcelClick={onParcelClickFromMap}
                    onViewInTable={parcels.length > 0 ? onViewInTable : undefined}
                    onGoToDeal={pipelineDeals.length > 0 ? goToDeal : undefined}
                    onAskAboutDeal={canWrite && pipelineDeals.length > 0 ? askAboutDeal : undefined}
                    popupWords={words.popup}
                  />
                </div>
              </section>
            )}

            {/* ── A tenant's version: every building on one line, a column per row of his spec ── */}
            {pipelineDeals.length > 0 && leaseSearch && (
              <section className="portal-section" id="section-terms">
                <h2>{words.terms}</h2>
                <PortalNotes
                  notes={notesFor('section', 'terms')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'terms')}
                />
                <p className="portal-note">
                  Every building on one line, against each line of your requirement. Each figure is what the listing or the
                  owner's material states; a blank means the building is not scored, and "not stated" means the source
                  does not say - not that the building falls short.
                </p>
                <LeaseSearchTable groups={glanceGroups} rows={scorecardRows} onGoToDeal={goToDeal} />
              </section>
            )}

            {/* ── Deal terms (VP8.4): every deal on one line ── */}
            {pipelineDeals.length > 0 && !leaseSearch && (
              <section className="portal-section" id="section-terms">
                <h2>{words.terms}</h2>
                <PortalNotes
                  notes={notesFor('section', 'terms')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'terms')}
                />
                <p className="portal-note">
                  Every deal on one line. Each figure is quoted from the investment analysis for that deal, its
                  offering memorandum or our emails to you, and a blank means the documents do not state it. The OM
                  cap is the seller's and our cap is the going-in cap our analysis printed; each names the price
                  it is at when that is not the ask. The five floors are
                  your criteria as each analysis scored them; a metric an analysis marks n/a leaves the count.
                  {' '}
                  {/* A screen's instructions; on paper there is nothing to click and nothing to scroll. */}
                  <span className="portal-no-print">
                    Click a column heading to sort; scroll the table sideways for the rest of its columns.
                  </span>
                </p>
                <PortalTermsTable
                  deals={termsDeals}
                  groups={pipelineGroups}
                  sourceNote={termsSourceNote}
                  onGoToDeal={goToDeal}
                />
              </section>
            )}

            {/* ── Parcels ── */}
            {parcels.length > 0 && (
              <section className="portal-section" id="section-parcels">
                <h2>Parcels</h2>
                <PortalNotes
                  notes={notesFor('section', 'parcels')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'parcels')}
                />
                {grouped.map(({ key, group, rows }) => (
                  <div
                    className="portal-group"
                    key={key}
                    style={{ borderLeftColor: group?.mapColor || 'var(--apex-line)' }}
                  >
                    <h3>{group?.label || humanize(key)}</h3>
                    {group?.description && <p className="portal-group-desc">{group.description}</p>}
                    <ParcelTable
                      parcels={rows}
                      showAssessed={showAssessed}
                      showTax={showTax}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                      renderNotes={canWrite ? renderParcelNotes : undefined}
                      selectedApn={selectedApn}
                      onLocate={locateOnMap}
                    />
                  </div>
                ))}
                {ungrouped.length > 0 && (
                  <div className="portal-group">
                    <h3>Other parcels</h3>
                    <ParcelTable
                      parcels={ungrouped}
                      showAssessed={showAssessed}
                      showTax={showTax}
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={toggleSort}
                      renderNotes={canWrite ? renderParcelNotes : undefined}
                      selectedApn={selectedApn}
                      onLocate={locateOnMap}
                    />
                  </div>
                )}
              </section>
            )}

            {/* ── Valuation ── */}
            {valuation && (
              <section className="portal-section" id="section-valuation">
                <h2>Valuation</h2>
                <PortalNotes
                  notes={notesFor('section', 'valuation')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'valuation')}
                />
                <dl className="portal-facts">
                  {valuation.method && (
                    <div className="portal-fact">
                      <dt>Method</dt>
                      <dd>{valuation.method}</dd>
                    </div>
                  )}
                  {moneyRange(valuation.realEstateLow, valuation.realEstateHigh) && (
                    <div className="portal-fact">
                      <dt>Real estate</dt>
                      <dd>{moneyRange(valuation.realEstateLow, valuation.realEstateHigh)}</dd>
                    </div>
                  )}
                  {money(valuation.recommendedList) && (
                    <div className="portal-fact">
                      <dt>Recommended list</dt>
                      <dd>{money(valuation.recommendedList)}</dd>
                    </div>
                  )}
                  {money(valuation.impliedPerAcre) && (
                    <div className="portal-fact">
                      <dt>Implied per acre</dt>
                      <dd>{money(valuation.impliedPerAcre)}</dd>
                    </div>
                  )}
                  {moneyRange(valuation.allInLow, valuation.allInHigh) && (
                    <div className="portal-fact">
                      <dt>All-in range</dt>
                      <dd>{moneyRange(valuation.allInLow, valuation.allInHigh)}</dd>
                    </div>
                  )}
                </dl>

                {/*
                  Business value is deliberately its own block BELOW the land numbers and outside
                  the headline. It is an unpriced going concern; the status string says so and is
                  printed verbatim rather than summarised.

                  TWO SHAPES, both live. The flat businessValue* fields are the original contract and
                  the stub fixture still uses them - deleting that path would silently empty the
                  fixture's callout, which is exactly the regression the acceptance bar exists to
                  catch. The `businessValue.views` object is the richer shape: it carries a SEPARATE
                  tier, so a deal can hold several attributed positions (an owner's figure, a figure
                  implied by an asking price) and serve them to the family while a buyer link sees
                  none of it. Neither shape invents a number; each view states who said it.
                */}
                {(moneyRange(valuation.businessValueLow, valuation.businessValueHigh) ||
                  valuation.businessValueStatus) && (
                  <div className="portal-callout">
                    <p className="portal-callout-label">Business value — additive, not included above</p>
                    {moneyRange(valuation.businessValueLow, valuation.businessValueHigh) && (
                      <p className="portal-callout-value">
                        {moneyRange(valuation.businessValueLow, valuation.businessValueHigh)}
                      </p>
                    )}
                    {valuation.businessValueStatus && (
                      <p className="portal-callout-sub">{valuation.businessValueStatus}</p>
                    )}
                  </div>
                )}

                {Array.isArray(valuation.businessValue?.views) &&
                  valuation.businessValue.views.length > 0 && (
                    <div className="portal-callout">
                      <p className="portal-callout-label">
                        {valuation.businessValue.label ?? 'Business value — additive, not included above'}
                      </p>
                      {valuation.businessValue.views.map((view, i) => (
                        <div className="portal-value-view" key={view.label ?? i}>
                          {view.label && <p className="portal-value-view-label">{view.label}</p>}
                          {moneyRange(view.low, view.high) && (
                            <p className="portal-callout-value">{moneyRange(view.low, view.high)}</p>
                          )}
                          {moneyRange(view.allInLow, view.allInHigh) && (
                            <p className="portal-callout-sub">
                              All-in with the land: {moneyRange(view.allInLow, view.allInHigh)}
                            </p>
                          )}
                          {view.basis && <p className="portal-value-view-basis">{view.basis}</p>}
                        </div>
                      ))}
                      {valuation.businessValue.status && (
                        <p className="portal-callout-sub">{valuation.businessValue.status}</p>
                      )}
                      {valuation.businessValue.note && (
                        <p className="portal-value-view-basis">{valuation.businessValue.note}</p>
                      )}
                    </div>
                  )}

                {valuation.note && <p className="portal-note">{valuation.note}</p>}
              </section>
            )}

            {/* ── Comparables (renders itself away when the block is absent; its heading is the contract's) ── */}
            <ComparableSales
              comps={deal.comps}
              renderRowExtra={
                canWrite
                  ? (item) => (
                      <PortalNotes
                        compact
                        notes={notesFor('comp', item.id)}
                        canWrite={canWrite}
                        onSubmit={submitNote('comp', item.id)}
                      />
                    )
                  : undefined
              }
            />

            {/* ── Net proceeds (family tier only; absent below it) ── */}
            {netProceeds && (
              <section className="portal-section" id="section-netProceeds">
                <h2>Net proceeds</h2>
                <PortalNotes
                  notes={notesFor('section', 'netProceeds')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'netProceeds')}
                />
                <div className="portal-fact-block">
                  <FactList value={{ ...netProceeds, rates: undefined }} />
                </div>
                {(netProceeds as { rates?: unknown }).rates ? (
                  <>
                    <h3>Rates applied</h3>
                    <div className="portal-fact-block">
                      {/* Rates arrive as fractions (0.133) - display as percentages. */}
                      <FactList
                        value={Object.fromEntries(
                          Object.entries(
                            (netProceeds as { rates: Record<string, unknown> }).rates
                          ).map(([k, v]) => [
                            k,
                            typeof v === 'number' ? Math.round(v * 1000) / 10 : v,
                          ])
                        )}
                        suffix="%"
                      />
                    </div>
                  </>
                ) : null}
                <p className="portal-note">
                  Estimate only, prepared for discussion. Confirm every figure with your CPA.
                </p>
              </section>
            )}

            {/* ── Deals (pipeline portals). On a lease search the lead group has already been drawn above the map. ── */}
            {restGroups.length + ungroupedDeals.length > 0 && (
              <section className="portal-section" id="section-deals">
                <h2>{words.deals}</h2>
                <PortalNotes
                  notes={notesFor('section', 'deals')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'deals')}
                />
                {deal.pipeline?.asOf && <p className="portal-subtitle">Status as of {deal.pipeline.asOf}</p>}
                {restBuckets.map(
                  ({ key, group, rows }) => (
                    /*
                      Each group is an accordion, OPEN by default (his ask, 2026-09-10: "let them start
                      with all the info... but if there's a bunch of files and Vin doesn't want to go
                      through all the analysed - no offer, he could just click the button").

                      A native <details> rather than a state flag, for three reasons: the keyboard and
                      screen-reader behaviour comes free, the printed handout can force every group open
                      with CSS alone, and `openCollapsedAncestors` - written for the map's "Go to deal"
                      when nothing on this page was collapsible yet - now has something real to open.
                      The summary carries the SAME h3 the heading had, so it stays a heading.
                    */
                    <details
                      className="portal-group"
                      key={key || 'other'}
                      // The phone menu's group entries jump here (TP7) - a tenant's page only, so Vin's DOM does not move.
                      id={leaseSearch ? groupElementId(key) : undefined}
                      open
                      style={{ borderLeftColor: group?.mapColor || 'var(--apex-line)' }}
                    >
                      <summary className="portal-group-summary">
                        <h3>
                          {groupLabel(key, group)}{' '}
                          <span className="portal-group-count">({rows.length})</span>
                        </h3>
                        {group?.description && <p className="portal-group-desc">{group.description}</p>}
                      </summary>
                      <div className="portal-deal-list">
                      {rows.map((d) => renderDealCard(d, group))}
                      </div>
                    </details>
                  )
                )}
              </section>
            )}

            {/* ── Asking rents by area (schema 1.3) - the broker's curated context, printed as authored ── */}
            {marketRows.length > 0 && (
              <section className="portal-section" id="section-market">
                <h2>{market?.title || 'Asking rents by area'}</h2>
                <PortalNotes
                  notes={notesFor('section', 'market')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'market')}
                />
                {market?.intro && <p className="portal-note">{market.intro}</p>}
                {market?.budgetLine && <p className="portal-market-budget">{market.budgetLine}</p>}
                <div className="portal-table-scroll">
                  <table className="portal-table portal-market-table">
                    <thead>
                      <tr>
                        {marketHasSegment && <th scope="col">Space</th>}
                        <th scope="col">Area</th>
                        <th scope="col">Asking rent</th>
                        {marketHasTypical && <th scope="col">Typical</th>}
                        <th scope="col">Based on</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Keyed by position: two rows may name the same area (shop space, NNN and gross). */}
                      {marketRows.map((r, i) => (
                        <tr key={i}>
                          {marketHasSegment && <td>{r.segment}</td>}
                          <th scope="row">{r.area}</th>
                          <td>{r.directAsking}</td>
                          {marketHasTypical && <td>{r.typical}</td>}
                          <td>{r.basis}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {marketTakeaways.length > 0 && (
                  <div className="portal-market-takeaways">
                    <p className="portal-lease-label">What this means for you</p>
                    <ul>
                      {marketTakeaways.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {market?.footnote && <p className="portal-note">{market.footnote}</p>}
                {market?.asOf && <p className="portal-subtitle">As of {market.asOf}</p>}
              </section>
            )}

            {/* ── Timeline (pipeline portals) ── */}
            {timelineItems.length > 0 && (
              <section className="portal-section" id="section-timeline">
                <h2>Timeline</h2>
                <PortalNotes
                  notes={notesFor('section', 'timeline')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'timeline')}
                />
                <ol className="portal-timeline">
                  {timelineItems.map((t, i) => (
                    <li className="portal-timeline-item" key={`${t.date ?? ''}-${i}`}>
                      <span className="portal-timeline-date">{t.date}</span>
                      <span className="portal-timeline-title">
                        {t.title}
                        {dealMark({ addedAt: t.addedAt }, [], since) === 'new' && (
                          <span className="portal-since-badge portal-since-new">New</span>
                        )}
                      </span>
                      {(t.dealIds ?? []).length > 0 && (
                        <span className="portal-timeline-deals">
                          {(t.dealIds ?? []).map((id) => (
                            <a className="portal-chip" href={`#deal-${id}`} key={id}>
                              {dealNameById.get(id) ?? id}
                            </a>
                          ))}
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* ── Documents ── */}
            {documents.length > 0 && (
              <section className="portal-section" id="section-documents">
                <h2>Documents</h2>
                <PortalNotes
                  notes={notesFor('section', 'documents')}
                  canWrite={canWrite}
                  onSubmit={submitNote('section', 'documents')}
                />
                {docGroups.map((g) => (
                  <div className="portal-doc-group" key={g.label}>
                    <h3>{g.label}</h3>
                    {g.docs.map((d) => {
                      const tail = (d.category ?? '').split(' · ').slice(1).join(' · ');
                      const metaLine = [d.doc_date, tail].filter(Boolean).join(' · ');
                      return (
                        <div className="portal-doc" key={d.id} data-testid={`portal-doc-${d.id}`}>
                          <div className="portal-doc-main">
                            <p className="portal-doc-title">
                              {d.title}
                              {docIsNew(d, since) && <span className="portal-since-badge">New</span>}
                            </p>
                            {metaLine && <p className="portal-doc-meta">{metaLine}</p>}
                          </div>
                          {d.status === 'available' ? (
                            <a
                              className="portal-download"
                              href={portalDocumentUrl(token ?? '', d.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Download
                            </a>
                          ) : (
                            <span className="portal-badge">Pending</span>
                          )}
                          <PortalNotes
                            compact
                            notes={notesFor('document', d.id)}
                            canWrite={canWrite}
                            onSubmit={submitNote('document', d.id)}
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </section>
            )}
          </>
        ))}

        {call && (
          <section className="portal-call-band" data-testid="portal-call-band" aria-label="Reach us">
            <a className="portal-call-btn" href={`tel:${call.e164}`}>Call {call.first}</a>
            <a className="portal-call-btn portal-call-btn--text" href={`sms:${call.e164}`}>Text {call.first}</a>
          </section>
        )}

        <footer className="portal-footer">
          <div className="portal-brokers">
            {/*
              Array order is the payload's order and is not re-sorted: the listing broker
              leads, and that ordering is a business fact carried by the contract.
            */}
            {brokers.map((b, i) => {
              const dre = (b.dre ?? '').trim();
              const dreLabel = dre ? (/^dre/i.test(dre) ? dre : `DRE ${dre}`) : '';
              const contact = [b.phone, b.email, b.web].filter(Boolean).join(' · ');
              return (
                <div key={`${b.name ?? 'broker'}-${i}`} data-testid="portal-broker">
                  <p className="portal-broker-name">{b.name}</p>
                  {(b.title || b.firm) && (
                    <p className="portal-broker-line">{[b.title, b.firm].filter(Boolean).join(', ')}</p>
                  )}
                  {dreLabel && <p className="portal-broker-line">{dreLabel}</p>}
                  {contact && <p className="portal-broker-line">{contact}</p>}
                </div>
              );
            })}
          </div>

          <div className="portal-legal">
            {meta.confidentialityNotice && <p>{meta.confidentialityNotice}</p>}
            {/* Schema 1.3: the contract's own disclosures - on a tenant's page, that Apex is not the listing broker. */}
            {(meta.disclosures ?? []).filter(Boolean).map((line) => (
              <p key={line}>{line}</p>
            ))}
            <p>
              Information contained herein has been obtained from sources deemed reliable but is
              not guaranteed. Figures marked REPORTED or UNVERIFIED have not been independently
              confirmed.
            </p>
            <p>
              This material is provided for informational purposes only and is not legal, tax, or
              investment advice. Consult your own attorney, CPA, and advisors before acting.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
